// What floats through the auth stage — the cells that open into something as
// they cross the middle of the pane. Each one is a small true thing about what
// lies past the door: a tool that reads the same memory, a memory netti keeps,
// a skill from the commons, a line on remembering.
//
// Memories and netti lines are ILLUSTRATIVE (the shape of what netti keeps, not
// anyone's record). Tools are only the clients innernet actually connects to
// over MCP (see /connections). Voices are real people only, quoted with their
// consent — the list stays empty until there is one; never write a stand-in.

export type StageCell =
  | { kind: "tool"; brand: string; name: string; line: string }
  | { kind: "memory"; line: string; meta: string }
  | { kind: "skill"; name: string; line: string }
  | { kind: "quote"; line: string; who: string }
  | { kind: "netti"; line: string; meta: string; fold?: number }
  | { kind: "private"; line: string; meta: string }
  | { kind: "voice"; line: string; who: string; role?: string };

const TOOLS: StageCell[] = [
  { kind: "tool", brand: "claude", name: "claude", line: "picks up where the last chat left off" },
  { kind: "tool", brand: "openai", name: "chatgpt", line: "reads the same memory claude writes" },
  { kind: "tool", brand: "cursor", name: "cursor", line: "knows your stack before you explain it" },
  { kind: "tool", brand: "codex", name: "codex", line: "starts every task with your decisions" },
  { kind: "tool", brand: "windsurf", name: "windsurf", line: "one memory, carried into the editor" },
  { kind: "tool", brand: "gemini", name: "gemini", line: "no more re-introducing yourself" },
  { kind: "tool", brand: "zed", name: "zed", line: "context that follows you between tools" },
  { kind: "tool", brand: "perplexity", name: "perplexity", line: "searches with what you already know" },
];

const MEMORIES: StageCell[] = [
  { kind: "memory", line: "prefers lowercase, quiet interfaces", meta: "design · remembered" },
  { kind: "memory", line: "ship the mac app before the extension", meta: "roadmap · decided" },
  { kind: "memory", line: "writes typescript, deploys on vercel", meta: "stack · remembered" },
  { kind: "memory", line: "deep work before noon, calls after", meta: "you · remembered" },
  { kind: "memory", line: "one door: mcp and rest share the handlers", meta: "architecture · decided" },
  { kind: "memory", line: "likes the short answer first, then the detail", meta: "you · remembered" },
  { kind: "memory", line: "ship the smaller version first, then listen", meta: "plan · decided" },
  { kind: "memory", line: "the launch moves to thursday", meta: "launch · decided" },
  { kind: "memory", line: "calls the dashboard home", meta: "words · remembered" },
];

const SKILLS: StageCell[] = [
  { kind: "skill", name: "tone of voice", line: "lowercase, calm, never hypey" },
  { kind: "skill", name: "weekly recap", line: "what moved, what's stuck, what's next" },
  { kind: "skill", name: "code review", line: "your standards, applied everywhere" },
  { kind: "skill", name: "meeting notes", line: "decisions first, then who owns what" },
  { kind: "skill", name: "launch checklist", line: "every step, in the order you trust" },
  { kind: "skill", name: "writing voice", line: "reads like you on a good day" },
];

const QUOTES: StageCell[] = [
  { kind: "quote", line: "memory is the treasury and guardian of all things.", who: "cicero" },
  { kind: "quote", line: "the true art of memory is the art of attention.", who: "samuel johnson" },
  { kind: "quote", line: "memory is the diary that we all carry about with us.", who: "oscar wilde" },
];

const NETTI: StageCell[] = [
  { kind: "netti", line: "folded three new decisions into your map", meta: "netti · just now", fold: 3 },
  { kind: "netti", line: "noticed you changed your mind, and kept the history", meta: "netti · today", fold: 2 },
  { kind: "netti", line: "found two notes saying the same thing and kept one", meta: "netti · yesterday", fold: 2 },
  { kind: "netti", line: "linked your sketch to the plan it belongs to", meta: "netti · this morning", fold: 1 },
];

/** the private ring: what you keep to yourself stays sealed until you allow an
 *  app, and every read is written down (lib/self · the private ring) */
const PRIVATE: StageCell[] = [
  { kind: "private", line: "your private facts reach only the apps you allow", meta: "private · sealed" },
  { kind: "private", line: "every time an app reads one, it is written down", meta: "private · on the record" },
];

/** Real words from real people, with their consent. Empty until there are some. */
export const VOICES: StageCell[] = [];

/** Interleaved so neighbours differ in kind — a tool, a memory, a quote … */
export function stageSequence(): StageCell[] {
  const pools = [TOOLS, MEMORIES, VOICES, PRIVATE, QUOTES, SKILLS, NETTI].filter((p) => p.length);
  const out: StageCell[] = [];
  const idx = pools.map(() => 0);
  const total = pools.reduce((n, p) => n + p.length, 0);
  let k = 0;
  while (out.length < total) {
    const p = k % pools.length;
    if (idx[p] < pools[p].length) out.push(pools[p][idx[p]++]);
    k++;
  }
  return out;
}
